import type { TableData } from '@/features/dashboard/api/schemas'
import { cx } from '@/shared/lib/classNames'
import { barGeometry, barScale, cellTone, formatCell } from './lib/tableCells'
import styles from './DataTable.module.css'

type DataTableProps = {
  table: TableData
  title: string
  unit?: string | null | undefined
}

/** A real table that formats what the backend sends; the first column stays put on a sideways scroll. */
export const DataTable = ({ table, title, unit }: DataTableProps) => {
  const { columns, rows } = table
  const scales = columns.map((column, index) => barScale(rows, column, index))
  return (
    <div className={styles.root}>
      <div
        className={styles.scroll}
        role='region'
        aria-label={`${title} table`}
        // Focusable so the keyboard can scroll it.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
      >
        <table className={styles.table}>
          <caption className={styles.caption}>{title}</caption>
          <thead>
            <tr>
              <th scope='col' className={cx(styles.head, styles.first)}>
                <span className={styles.hidden}>Row</span>
              </th>
              {columns.map(column => (
                <th
                  key={column.id}
                  scope='col'
                  className={cx(
                    styles.head,
                    column.kind === 'text' ? styles.text : styles.num
                  )}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={row.id}
                className={cx(
                  styles.row,
                  row.kind === 'group' && styles.group,
                  row.kind === 'total' && styles.total
                )}
              >
                <th
                  scope='row'
                  className={cx(styles.first, row.level === 1 && styles.child)}
                >
                  {row.label}
                </th>
                {columns.map((column, index) => {
                  const cell = row.cells[index] ?? null
                  const tone = cellTone(cell, column)
                  const scale = scales[index]
                  if (scale && typeof cell === 'number' && row.kind === 'row') {
                    const bar = barGeometry(cell, scale)
                    return (
                      <td
                        key={column.id}
                        className={cx(styles.num, styles.barCell)}
                      >
                        <span className={styles.barTrack} aria-hidden='true'>
                          {scale.mode === 'diverge' && (
                            <span className={styles.barAxis} />
                          )}
                          <span
                            className={cx(
                              styles.barFill,
                              bar.below && styles.barBelow
                            )}
                            style={{
                              left: `${bar.left}%`,
                              width: `${bar.width}%`,
                            }}
                          />
                        </span>
                        <span className={styles.barFigure}>
                          {formatCell(cell, column, unit)}
                        </span>
                      </td>
                    )
                  }
                  return (
                    <td
                      key={column.id}
                      className={cx(
                        column.kind === 'text' ? styles.text : styles.num,
                        styles[tone]
                      )}
                    >
                      {formatCell(cell, column, unit)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
